<script setup lang="ts">
import type { UploadFrame } from "@/types/frames";
import { ref, onMounted, computed } from "vue";
import { useRouter } from "vue-router";
import type { PnFileUpload } from "@postnord/web-components/components/pn-file-upload";
import type { PnUploadFileItem } from "@postnord/web-components/types/globals/types";
import { v4 as uuidv4 } from "uuid";
import { useFrames } from "@/stores/frames";

const router = useRouter();
const uploadError = ref("");
const uploadLog = ref("");
const hidden = ref(false);
const filecount = ref(0);
const englishDescription = ref("");
const swedishDescription = ref("");
const isError = ref(false);
const errorDescription = ref("");
const englishDescriptionError = ref("");
const swedishDescriptionError = ref("");
const showSpinner = ref(false);
const framesStore = useFrames();

const languages = ref({
  swedish: true,
  english: true,
  norwegian: true,
  danish: true,
} as Record<string, boolean>);

const getLanguages = computed(() => {
  const l = [];
  if (languages.value.swedish) {
    l.push("swedish");
  }
  if (languages.value.english) {
    l.push("english");
  }
  if (languages.value.danish) {
    l.push("danish");
  }
  if (languages.value.norwegian) {
    l.push("norwegian");
  }
  return l;
});

const filesToUpload = ref<PnUploadFileItem[]>([]);

const languageChange = (e: any) => {
  const lang = e.target.id;
  languages.value[lang] = e.target.checked;
};

const filesAdded = (e: any) => {
  if (e.detail.length > 0) {
    const errorFiles = e.detail.filter((d: any) => d.error);

    if (errorFiles.length > 0) {
      return;
    } else {
      resetError();
      filecount.value = e.detail.length;
      filesToUpload.value = e.detail;
    }
  } else if (e.detail.length === 0) {
    filecount.value = e.detail.length;
    resetError();
    filesToUpload.value = e.detail;
  }
};

const resetError = () => {
  isError.value = false;
  errorDescription.value = "";
  swedishDescriptionError.value = "";
  englishDescriptionError.value = "";
  window.scrollTo(0, 0);
};

const uploadFile = async (file: PnUploadFileItem, groupId: string) => {
  const lang = getLanguages.value;
  uploadLog.value += `Saving frame ${file.filename}\n`;
  try {
    showSpinner.value = true;
    const uploadObj: UploadFrame = {
      groupId,
      filename: file.filename,
      base64: file.base64,
      hidden: hidden.value,
      languages: lang,
      categories: [],
      imageDescription: [
        {
          language: "swedish",
          description: swedishDescription.value,
        },
        {
          language: "english",
          description: englishDescription.value,
        },
      ],
    };
    await framesStore.updateFrame("", uploadObj);
    filecount.value -= 1;
    uploadLog.value += `Remaining files: ${filecount.value}\n`;
    if (filecount.value === 0) {
      router.push("frames");
    }
  } catch (e) {
    isError.value = true;
    errorDescription.value =
      "There was an error in uploading one or all the images";
    window.scrollTo(0, 0);
  } finally {
    showSpinner.value = false;
  }
};

const uploadAllFiles = async () => {
  const isValidationError =
    swedishDescription.value === "" ||
    englishDescription.value === "" ||
    filecount.value !== 3;
  if (swedishDescription.value === "") {
    swedishDescriptionError.value = "Please enter swedish alternative text";
  } else {
    swedishDescriptionError.value = "";
  }
  if (englishDescription.value === "") {
    englishDescriptionError.value = "Please enter english alternative text";
  } else {
    englishDescriptionError.value = "";
  }
  if (filecount.value !== 3) {
    isError.value = true;
    errorDescription.value = "Please upload 3 frames for different sizes.";
    window.scrollTo(0, 0);
  }

  if (!isValidationError) {
    resetError();
    const groupId = uuidv4();
    for (const file of filesToUpload.value) {
      await uploadFile(file, groupId);
    }
    if (filecount.value === 0) {
      if (isError.value) {
        framesStore.setNotificationAlert({
          show: true,
          type: "error",
          message: "There was an error in uploading one or more frames",
          autoHide: true,
        });
      } else {
        framesStore.setNotificationAlert({
          show: true,
          message: "Frames uploaded successfully",
          type: "success",
          autoHide: true,
        });
      }
      router.push("frames");
    }
  }
};

onMounted(async () => {
  resetError();
});
</script>

<template>
  <pn-toast
    appearance="error"
    v-show="isError"
    :text="errorDescription"
  ></pn-toast>
  <h2>Upload new frames</h2>
  <div :class="[{ spinner: showSpinner }]">
    <pn-spinner size="3" v-show="showSpinner" />
  </div>
  <h3>Step 1, select languages:</h3>
  <p v-show="!uploadLog">
    <label for="swedish">
      <pn-checkbox
        checkboxid="swedish"
        :checked="languages.swedish"
        @change="languageChange"
      />
      Swedish
    </label>
    <label for="english">
      <pn-checkbox
        checkboxid="english"
        :checked="languages.english"
        @change="languageChange"
      />
      English
    </label>
    <label for="danish">
      <pn-checkbox
        checkboxid="danish"
        :checked="languages.danish"
        @change="languageChange"
      />
      Danish
    </label>
    <label for="norwegian">
      <pn-checkbox
        checkboxid="norwegian"
        :checked="languages.norwegian"
        @change="languageChange"
      />
      Norwegian
    </label>
  </p>

  <p>
    <label for="hidden">
      <pn-checkbox
        checkboxid="hidden"
        :checked="hidden"
        @change="hidden = $event.target.checked"
      />
      Upload frames as hidden
    </label>
  </p>

  <h3>Step 2: Upload</h3>
  <div v-show="uploadLog">
    <pre>{{ uploadLog }}</pre>
  </div>
  <div v-show="!getLanguages.length">
    <pn-toast closable="false" appearance="error">
      Must select at least one language to upload
    </pn-toast>
  </div>
  <div v-show="getLanguages.length && !uploadLog">
    <span>Number of valid files uploaded {{ filesToUpload.length }}</span>
    <pn-file-upload
      accept=".png"
      max-size="5mb"
      disabled="false"
      multiple="yes"
      limit="3"
      show-spinner="true"
      helpertext="You can upload multiple frames at the same time(Image dimensions 2539 * 1807 px, transparent background)."
      @filesAdded="filesAdded"
    >
      <pn-toast
        text="Please upload all 3 frames for all different sizes at the same time."
      ></pn-toast>
    </pn-file-upload>
    <pn-toast appearance="warning" v-show="uploadError">{{
      uploadError
    }}</pn-toast>
  </div>
  <div>
    <h3>Step 4, add alternative text (for accessibility purposes)</h3>
    <pn-input
      placeholder="Swedish alternative text (required*)"
      v-model="swedishDescription"
      :error="swedishDescriptionError"
      required
    />
    <br /><br />
    <pn-input
      placeholder="English alternative text (required*)"
      v-model="englishDescription"
      :error="englishDescriptionError"
      required
    />
  </div>
  <br />
  <pn-button @click="uploadAllFiles">Upload Frame</pn-button>
</template>

<style lang="scss" scoped>
img {
  border: 1px dashed gray;
  border-radius: 1rem;
  width: 300px;
}
.icon {
  width: 200px;
}

.spinner {
  width: 100%;
  height: 100%;
  position: fixed;
  left: 0;
  top: 0;
  background: rgba(0, 0, 0, 0.4);
  z-index: 99;
  display: flex;
  justify-content: center;
  align-items: center;
}
</style>
